import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { RegionCode, CurrencyCode } from '../types/region';
import { REGIONS, CURRENCIES } from '../utils/currencyData';
import { getPriceForRegion, formatCurrency } from '../utils/currencyConverter';
import { initializeLocale, saveLocalePreference, getSavedLocale } from '../utils/localeDetection';

interface RegionContextType {
  // Current region and currency
  region: RegionCode;
  currency: CurrencyCode;
  
  // Region and currency details
  regionName: string;
  currencySymbol: string;
  currencyName: string;
  flag: string;
  locale: string;
  
  // Actions
  setRegion: (region: RegionCode) => void;
  
  // Utility functions
  formatPrice: (amountInINR: number) => string;
  getPriceInRegion: (amountInINR: number) => { amount: number; currency: CurrencyCode; formatted: string };
  
  // Loading state
  isLoading: boolean;
}

const RegionContext = createContext<RegionContextType | undefined>(undefined);

interface RegionProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = 'trainzilla_region_preference';

export function RegionProvider({ children }: RegionProviderProps) {
  // Fallback until geo-detection resolves (or when it's unavailable, e.g. prerender).
  const [region, setRegionState] = useState<RegionCode>('US');
  const [isLoading, setIsLoading] = useState(true);

  // Initialize region from localStorage or detect from browser
  useEffect(() => {
    const initializeRegion = async () => {
      try {
        // Use unified locale detection - now asynchronous for GEO detection
        const { region: detectedRegion } = await initializeLocale();
        console.log('📍 Region initialized:', detectedRegion);
        setRegionState(detectedRegion);
      } catch (error) {
        console.error('Error initializing region:', error);
        setRegionState('US');
      } finally {
        setIsLoading(false);
      }
    };

    initializeRegion();
  }, []);

  // Update region and save to localStorage
  const setRegion = (newRegion: RegionCode) => {
    console.log('📍 Region changed to:', newRegion);
    setRegionState(newRegion);
    
    try {
      localStorage.setItem(STORAGE_KEY, newRegion);
      // Note: Language will be saved separately by LanguageContext
    } catch (error) {
      console.error('Error saving region to localStorage:', error);
    }
  };

  // Get current region and currency details
  const currentRegion = REGIONS[region];
  const currentCurrency = CURRENCIES[currentRegion.currency];

  // Utility functions
  const formatPrice = (amountInINR: number): string => {
    const priceData = getPriceForRegion(amountInINR, region);
    return priceData.formatted;
  };

  const getPriceInRegion = (amountInINR: number) => {
    return getPriceForRegion(amountInINR, region);
  };

  const value: RegionContextType = {
    region,
    currency: currentRegion.currency,
    regionName: currentRegion.name,
    currencySymbol: currentCurrency.symbol,
    currencyName: currentCurrency.name,
    flag: currentRegion.flag,
    locale: currentRegion.locale,
    setRegion,
    formatPrice,
    getPriceInRegion,
    isLoading
  };

  return (
    <RegionContext.Provider value={value}>
      {children}
    </RegionContext.Provider>
  );
}

export function useRegion(): RegionContextType {
  const context = useContext(RegionContext);
  if (context === undefined) {
    throw new Error('useRegion must be used within a RegionProvider');
  }
  return context;
}

export default RegionContext;